import { DataGlueGraph, DataConnection, NodeInstance } from '@glueapp/graph-types';
import { ValidationError, ValidationRuleResult } from '../ValidationRule';

const RESPONSE_NODE_IDS = ['RespondToApi', 'RespondToApiStream'];

interface EndpointDefinition {
    id: string;
    streaming: boolean;
}

export const ApiResponseContextRule = (graph: DataGlueGraph): ValidationRuleResult => {
    const errors: ValidationError[] = [];

    // Check main graph
    errors.push(...validateResponseNodes(graph.nodeInstances, graph.connections, 'main'));

    // Check function graphs
    if (graph.functions) {
        for (const func of graph.functions) {
            errors.push(...validateResponseNodes(func.nodeInstances, func.connections, func.id));
        }
    }

    return { valid: errors.length === 0, errors };
};

function validateResponseNodes(nodeInstances: NodeInstance[], connections: DataConnection[], context: string): ValidationError[] {
    const errors: ValidationError[] = [];

    // Build reverse adjacency map: receivingNodeInstanceId → sendingNodeInstanceIds
    const reverseAdjacency = new Map<number, Set<number>>();
    for (const connection of connections) {
        if (!reverseAdjacency.has(connection.receivingNodeInstanceId)) {
            reverseAdjacency.set(connection.receivingNodeInstanceId, new Set());
        }
        reverseAdjacency.get(connection.receivingNodeInstanceId)!.add(connection.sendingNodeInstanceId);
    }

    // Build node lookup by id
    const nodeById = new Map<number, NodeInstance>();
    for (const node of nodeInstances) {
        nodeById.set(node.id, node);
    }

    // Find all response nodes
    const responseNodes = nodeInstances.filter(node => RESPONSE_NODE_IDS.includes(node.nodeId));

    for (const responseNode of responseNodes) {
        // BFS backwards to find all ancestor nodes
        const visited = new Set<number>();
        const queue = [responseNode.id];
        visited.add(responseNode.id);

        while (queue.length > 0) {
            const currentId = queue.shift()!;
            const parents = reverseAdjacency.get(currentId);
            if (parents) {
                for (const parentId of parents) {
                    if (!visited.has(parentId)) {
                        visited.add(parentId);
                        queue.push(parentId);
                    }
                }
            }
        }

        // Check if any ancestor is an ApiServer
        const ancestorApiServers = nodeInstances.filter(
            node => node.nodeId === 'ApiServer' && visited.has(node.id) && node.id !== responseNode.id,
        );

        if (ancestorApiServers.length === 0) {
            const location =
                context === 'main' ? { nodeId: responseNode.id, context: 'main graph' } : { nodeId: responseNode.id, functionId: context };
            errors.push({
                code: 'RESPOND_WITHOUT_API_SERVER',
                message: `'${responseNode.nodeId === 'RespondToApi' ? 'Respond to API' : 'Respond to API (Stream)'}' node must be downstream from an API Server node.`,
                severity: 'error',
                location,
            });
            continue;
        }

        // Check streaming mismatch
        for (const apiServer of ancestorApiServers) {
            const endpoints = (apiServer.state?.endpoints as unknown as EndpointDefinition[]) || [];

            if (responseNode.nodeId === 'RespondToApi') {
                // Non-streaming response should not be downstream from streaming endpoints
                const hasStreamingEndpoint = endpoints.some(endpoint => endpoint.streaming);
                if (hasStreamingEndpoint) {
                    const location =
                        context === 'main'
                            ? { nodeId: responseNode.id, context: 'main graph' }
                            : { nodeId: responseNode.id, functionId: context };
                    errors.push({
                        code: 'STREAMING_MISMATCH',
                        message: `'Respond to API' node cannot be used with streaming endpoints. Use 'Respond to API (Stream)' instead.`,
                        severity: 'error',
                        location,
                    });
                }
            } else if (responseNode.nodeId === 'RespondToApiStream') {
                // Streaming response should not be downstream from non-streaming endpoints
                const hasNonStreamingEndpoint = endpoints.some(endpoint => !endpoint.streaming);
                if (hasNonStreamingEndpoint) {
                    const location =
                        context === 'main'
                            ? { nodeId: responseNode.id, context: 'main graph' }
                            : { nodeId: responseNode.id, functionId: context };
                    errors.push({
                        code: 'STREAMING_MISMATCH',
                        message: `'Respond to API (Stream)' node cannot be used with non-streaming endpoints. Use 'Respond to API' instead.`,
                        severity: 'error',
                        location,
                    });
                }
            }
        }
    }

    return errors;
}
